import { useCallback, useMemo, useState } from "react";
import { Avatar, Button, Dropdown, Typography, theme } from "antd";
import type { MenuProps } from "antd";
import { DownOutlined, LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { NavLink, useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { Notify } from "../../utils/constants";
import { apiClient } from "../../utils/httpClient";
import "./Header.scss";

const faviconSrc = `${process.env.PUBLIC_URL}/favicon.ico`;

const Header = () => {
  const { token } = theme.useToken();
  const { authUser, clearAuth, setRoles } = useUser();
  const navigate = useNavigate();

  const isDev = false;
  const [role, setRole] = useState(isDev ? "ROLE_MANAGER" : "");

  const logout = useCallback(async () => {
    try {
      await apiClient.post(
        `${process.env.REACT_APP_API_URL}/logout`,
        {},
        { maxRedirects: 0 },
      );

      clearAuth();
      navigate("/login");
    } catch (error: unknown) {
      const logoutError = error as {
        response?: { status?: number; headers?: { location?: string } };
      };

      if (logoutError.response?.status === 302) {
        const redirectUrl = logoutError.response.headers?.location || "/login";
        window.location.href = redirectUrl;
        return;
      }

      console.error("Logout error:", error);
      Notify("error", "Logout failed. Please try again.");
    }
  }, [clearAuth, navigate]);

  const userMenuItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "profile",
        label: "My profile",
        onClick: () => navigate("/profile"),
        icon: <UserOutlined />,
      },
      { type: "divider" },
      {
        key: "logout",
        label: "Logout",
        onClick: logout,
        icon: <LogoutOutlined />,
      },
    ],
    [logout, navigate],
  );

  const userInitials = useMemo(() => {
    const nameParts = authUser?.name?.trim().split(/\s+/) ?? [];
    return nameParts
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  }, [authUser?.name]);

  return (
    <header
      className="app-header"
      style={{
        background: token.colorBgElevated,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        boxShadow: token.boxShadowTertiary,
      }}
    >
      <div className="app-header__brand">
        <NavLink to="/" style={{ color: token.colorText }}>
          <img
            className="app-header__logo"
            src={faviconSrc}
            alt=""
            aria-hidden="true"
          />
          <span>HolidayApp</span>
        </NavLink>
      </div>

      <div className="app-header__actions">
        {isDev && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Typography.Text style={{ color: token.colorTextSecondary }}>
              Role for debugging:
            </Typography.Text>

            <div style={{ display: "flex", gap: 8 }}>
              <Button
                type={role === "ROLE_MANAGER" ? "primary" : "default"}
                onClick={() => {
                  setRole("ROLE_MANAGER");
                  setRoles(["ROLE_MANAGER"]);
                }}
              >
                MANAGER
              </Button>
              <Button
                type={role === "ROLE_HR" ? "primary" : "default"}
                onClick={() => {
                  setRole("ROLE_HR");
                  setRoles(["ROLE_HR"]);
                }}
              >
                HR
              </Button>
              <Button
                type={role === "ROLE_EMPLOYEE" ? "primary" : "default"}
                onClick={() => {
                  setRole("ROLE_EMPLOYEE");
                  setRoles(["ROLE_EMPLOYEE"]);
                }}
              >
                EMPLOYEE
              </Button>
              <Button
                type={role === "ROLE_ADMIN" ? "primary" : "default"}
                onClick={() => {
                  setRole("ROLE_ADMIN");
                  setRoles(["ROLE_ADMIN"]);
                }}
              >
                ADMIN
              </Button>
            </div>
          </div>
        )}

        <Dropdown
          menu={{ items: userMenuItems, className: "profile-menu" }}
          trigger={["hover"]}
          placement="bottomRight"
          arrow
        >
          <Button
            className="app-header__profile"
            aria-label="Open profile menu"
            style={{
              borderColor: token.colorBorderSecondary,
              background: token.colorBgContainer,
            }}
          >
            <Avatar size={32} className="app-header__avatar">
              {userInitials || <UserOutlined />}
            </Avatar>
            <span className="app-header__greeting">
              <small>Welcome back</small>
              <strong>{authUser?.name ?? "My account"}</strong>
            </span>
            <DownOutlined className="app-header__chevron" />
          </Button>
        </Dropdown>
      </div>
    </header>
  );
};

export default Header;
